// P1 catalogue and partners (CAT-C-05): reads from api_v1 views, writes through operation functions.
// Prices come from their own view, which has rows only for the owner and Sellers (ADR-088); costs only for the owner.
import { Refusal } from './auth';
import { sendCommand } from './commands';
import { supabase } from './supabase';

export type Unit = 'piece' | 'box';

export interface Product {
  id: string;
  name: string;
  sku: string | null;
  category_id: string | null;
  category_path: string | null;
  brand_id: string | null;
  brand: string | null;
  box_capacity: number | null;
  sell_piece: boolean;
  sell_box: boolean;
  reorder_level: number;
  notes: string | null;
  active: boolean;
  has_history: boolean;
  /** PRD-12: the photo's path in the private bucket, or null. */
  image_path: string | null;
  /** PRD-07: the family of a variant and its attribute values, in the family's order. */
  family_id: string | null;
  family_name: string | null;
  attr_values: string[] | null;
  /** PRD-08: a bundle has no stock; its components and pieces per bundle. */
  is_bundle: boolean;
  components: { product_id: string; name: string; sku: string | null; quantity: number }[] | null;
  /** PRD-09: lot tracking (permanent) and how many lots with pieces have expired. */
  lot_tracked: boolean;
  expired_lots: number;
}
export interface ProductPrice {
  product_id: string;
  piece_price: number | null;
  box_price: number | null;
}
export interface ProductCost {
  product_id: string;
  avg_cost: number | null;
  last_purchase_price: number | null;
}
export interface PriceLine {
  id: number;
  product_id: string;
  unit: Unit;
  old_price: number | null;
  new_price: number;
  reason: string | null;
  changed_at: string;
  changed_by_name: string | null;
}
export interface Barcode {
  code: string;
  product_id: string;
  unit: Unit;
  source: 'supplier' | 'internal';
}
export interface Category {
  id: string;
  name: string;
  parent_id: string | null;
  path: string;
  depth: number;
  product_count: number;
}
export interface Brand {
  id: string;
  name: string;
  product_count: number;
}
export interface Supplier {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  tax_ref: string | null;
  payment_term_days: number | null;
  notes: string | null;
  active: boolean;
  products_bought: number;
  open_orders: number;
  late_orders: number;
  purchases_this_year: number | null;
  has_history: boolean;
}
export interface CustomerGroup {
  id: string;
  name: string;
  active: boolean;
  customer_count: number;
}
export interface Customer {
  id: string;
  name: string;
  group_id: string | null;
  group_name: string | null;
  area: string | null;
  phone: string | null;
  contact: string | null;
  email: string | null;
  credit_limit: number;
  notes: string | null;
  active: boolean;
  anonymized: boolean;
  unpaid_total: number;
  overdue_total: number;
  sales_count: number;
  last_purchase: string | null;
  /** CUS-07: public = every seller sees it; private = its creator and the owner. */
  visibility: 'public' | 'private';
  created_by: string | null;
  created_by_name: string | null;
  /** True when the signed-in user created it. */
  mine: boolean | null;
}

function rows<T>(res: { data: unknown; error: { message: string } | null }): T[] {
  if (res.error) throw new Refusal('SERVER_ERROR');
  return (res.data ?? []) as T[];
}
const num = (v: unknown) => (v === null || v === undefined ? null : Number(v));

export async function loadProducts(): Promise<Product[]> {
  return rows<Product>(await supabase.from('v_products').select('*').order('name'));
}
export async function loadPrices(): Promise<Map<string, ProductPrice>> {
  const list = rows<ProductPrice>(await supabase.from('v_product_prices').select('*'));
  return new Map(
    list.map((p) => [p.product_id, { ...p, piece_price: num(p.piece_price), box_price: num(p.box_price) }]),
  );
}
export async function loadCosts(): Promise<Map<string, ProductCost>> {
  const list = rows<ProductCost>(await supabase.from('v_product_costs').select('*'));
  return new Map(
    list.map((c) => [
      c.product_id,
      { ...c, avg_cost: num(c.avg_cost), last_purchase_price: num(c.last_purchase_price) },
    ]),
  );
}
export async function loadPriceHistory(product: string): Promise<PriceLine[]> {
  return rows<PriceLine>(
    await supabase
      .from('v_price_history')
      .select('*')
      .eq('product_id', product)
      .order('changed_at', { ascending: false }),
  ).map((l) => ({ ...l, old_price: num(l.old_price), new_price: Number(l.new_price) }));
}
export async function loadBarcodes(product?: string): Promise<Barcode[]> {
  const q = supabase.from('v_barcodes').select('code, product_id, unit, source');
  return rows<Barcode>(await (product ? q.eq('product_id', product) : q));
}
export async function findBarcode(code: string): Promise<Barcode | null> {
  const list = rows<Barcode>(
    await supabase.from('v_barcodes').select('code, product_id, unit, source').eq('code', code),
  );
  return list[0] ?? null;
}
export async function loadCategories(): Promise<Category[]> {
  return rows<Category>(await supabase.from('v_categories').select('*'));
}
export async function loadBrands(): Promise<Brand[]> {
  return rows<Brand>(await supabase.from('v_brands').select('*'));
}
export async function loadSuppliers(): Promise<Supplier[]> {
  return rows<Supplier>(await supabase.from('v_suppliers').select('*').order('name'));
}
export async function loadCustomerGroups(): Promise<CustomerGroup[]> {
  return rows<CustomerGroup>(await supabase.from('v_customer_groups').select('*'));
}
export async function loadCustomers(): Promise<Customer[]> {
  return rows<Customer>(await supabase.from('v_customers').select('*').order('name')).map((c) => ({
    ...c,
    credit_limit: Number(c.credit_limit),
    unpaid_total: Number(c.unpaid_total),
    overdue_total: Number(c.overdue_total),
  }));
}

export interface ProductInput {
  id: string | null;
  name: string;
  sku: string;
  category: string | null;
  brand: string | null;
  boxCapacity: number | null;
  sellPiece: boolean;
  sellBox: boolean;
  piecePrice: number | null;
  boxPrice: number | null;
  reorderLevel: number;
  notes: string;
}
export const saveProduct = (p: ProductInput) =>
  sendCommand<{ id: string }>('save_product', {
    p_id: p.id,
    p_name: p.name,
    p_sku: p.sku,
    p_category: p.category,
    p_brand: p.brand,
    p_box_capacity: p.boxCapacity,
    p_sell_piece: p.sellPiece,
    p_sell_box: p.sellBox,
    p_piece_price: p.piecePrice,
    p_box_price: p.boxPrice,
    p_reorder_level: p.reorderLevel,
    p_notes: p.notes,
  });
export const changePrice = (product: string, unit: Unit, price: number, reason: string) =>
  sendCommand('change_price', { p_product: product, p_unit: unit, p_price: price, p_reason: reason });
export const addBarcode = (product: string, unit: Unit, code: string, source: 'supplier' | 'internal') =>
  sendCommand<{ code: string }>('add_barcode', { p_product: product, p_unit: unit, p_code: code, p_source: source });
export const generateBarcode = (product: string, unit: Unit) =>
  sendCommand<{ code: string }>('generate_barcode', { p_product: product, p_unit: unit });
export const toggleProduct = (product: string, active: boolean) =>
  sendCommand('toggle_product', { p_product: product, p_active: active });
export const deleteProduct = (product: string) => sendCommand('delete_product', { p_product: product });
export const saveCategory = (id: string | null, name: string, parent: string | null) =>
  sendCommand<{ id: string }>('save_category', { p_id: id, p_name: name, p_parent: parent });
export const saveBrand = (id: string | null, name: string) =>
  sendCommand<{ id: string }>('save_brand', { p_id: id, p_name: name });

export interface SupplierInput {
  id: string | null;
  name: string;
  phone: string;
  email: string;
  address: string;
  taxRef: string;
  paymentTermDays: number | null;
  notes: string;
}
export const saveSupplier = (s: SupplierInput) =>
  sendCommand<{ id: string }>('save_supplier', {
    p_id: s.id,
    p_name: s.name,
    p_phone: s.phone,
    p_email: s.email,
    p_address: s.address,
    p_tax_ref: s.taxRef,
    p_payment_term_days: s.paymentTermDays,
    p_notes: s.notes,
  });
export const toggleSupplier = (id: string, active: boolean) =>
  sendCommand('toggle_supplier', { p_supplier: id, p_active: active });
export const deleteSupplier = (id: string) => sendCommand('delete_supplier', { p_supplier: id });

export interface CustomerInput {
  id: string | null;
  name: string;
  group: string | null;
  area: string;
  phone: string;
  contact: string;
  email: string;
  creditLimit: number;
  notes: string;
  visibility: 'public' | 'private';
}
export const saveCustomer = (c: CustomerInput) =>
  sendCommand<{ id: string }>('save_customer', {
    p_id: c.id,
    p_name: c.name,
    p_group: c.group,
    p_area: c.area,
    p_phone: c.phone,
    p_contact: c.contact,
    p_email: c.email,
    p_credit_limit: c.creditLimit,
    p_notes: c.notes,
    p_visibility: c.visibility,
  });
export const toggleCustomer = (id: string, active: boolean) =>
  sendCommand('toggle_customer', { p_customer: id, p_active: active });
export const saveCustomerGroup = (name: string) => sendCommand<{ id: string }>('save_customer_group', { p_name: name });
export const toggleCustomerGroup = (id: string, active: boolean) =>
  sendCommand('toggle_customer_group', { p_group: id, p_active: active });

/* ------------------------------------------------------------------ PRD-12 / ADR-111: product photo */

export const PHOTO_BUCKET = 'product-images';

/** Uploads a shrunk photo, points the product at it, then deletes the photo it replaced. */
export async function saveProductPhoto(product: string, photo: Blob, ext: 'webp' | 'jpg'): Promise<string> {
  const path = `${product}/${crypto.randomUUID()}.${ext}`;
  const bucket = supabase.storage.from(PHOTO_BUCKET);
  const up = await bucket.upload(path, photo, { contentType: photo.type, upsert: false });
  if (up.error) throw new Refusal(/fetch/i.test(up.error.message) ? 'OFFLINE' : 'PHOTO_UPLOAD_FAILED');
  let result: { old_path: string | null };
  try {
    result = await sendCommand<{ old_path: string | null }>('set_product_image', { p_product: product, p_path: path });
  } catch (e) {
    await bucket.remove([path]);
    throw e;
  }
  if (result.old_path) await bucket.remove([result.old_path]);
  return path;
}

export async function removeProductPhoto(product: string): Promise<void> {
  const result = await sendCommand<{ old_path: string | null }>('set_product_image', {
    p_product: product,
    p_path: null,
  });
  if (result.old_path) await supabase.storage.from(PHOTO_BUCKET).remove([result.old_path]);
}

/* ------------------------------------------------------------------ PRD-13 / ADR-154: the product gallery */

export interface ProductImage {
  id: string;
  product_id: string;
  path: string;
  position: number;
}
export async function loadProductImages(product: string): Promise<ProductImage[]> {
  return rows<ProductImage>(
    await supabase.from('v_product_images').select('*').eq('product_id', product).order('position'),
  );
}

/** Uploads a shrunk photo and adds it to the product's gallery (the first one becomes the cover). */
export async function addProductPhoto(product: string, photo: Blob, ext: 'webp' | 'jpg'): Promise<void> {
  const path = `${product}/${crypto.randomUUID()}.${ext}`;
  const bucket = supabase.storage.from(PHOTO_BUCKET);
  const up = await bucket.upload(path, photo, { contentType: photo.type, upsert: false });
  if (up.error) throw new Refusal(/fetch/i.test(up.error.message) ? 'OFFLINE' : 'PHOTO_UPLOAD_FAILED');
  try {
    await sendCommand('add_product_image', { p_product: product, p_path: path });
  } catch (e) {
    await bucket.remove([path]);
    throw e;
  }
}

export async function removeProductImage(image: string): Promise<void> {
  const result = await sendCommand<{ old_path: string | null }>('remove_product_image', { p_image: image });
  if (result.old_path) await supabase.storage.from(PHOTO_BUCKET).remove([result.old_path]);
}

export const makeCoverImage = (image: string) => sendCommand('make_cover_image', { p_image: image });

/** Short-lived links for the photos on screen, asked for in one call. */
export async function photoLinks(paths: string[]): Promise<Map<string, string>> {
  if (paths.length === 0) return new Map();
  const { data, error } = await supabase.storage.from(PHOTO_BUCKET).createSignedUrls(paths, 3600);
  if (error || !data) return new Map();
  return new Map(data.filter((d) => d.signedUrl && d.path).map((d) => [d.path as string, d.signedUrl]));
}
